import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

function CheckoutField({ name, label, type = "text", required, value, error, onChange }) {
  return (
    <div>
      <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        data-testid={`checkout-${name}`}
        type={type}
        value={value || ""}
        onChange={e => onChange(name, e.target.value)}
        className={`w-full px-4 py-3 bg-surface border rounded-sm text-foreground text-sm focus:outline-none transition-colors placeholder:text-foreground/20 ${error ? "border-red-400" : "border-border focus:border-foreground/40"}`}
      />
      {error && <p className="text-xs text-red-400 font-mono mt-1">{error}</p>}
    </div>
  );
}

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    zip: "",
    country: "Deutschland",
    notes: ""
  });

  useEffect(() => {
    if (user?.email && !form.email) {
      setForm(f => ({ ...f, email: user.email }));
    }
  }, [user]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const validate = () => {
    const e = {};
    if (!form.first_name) e.first_name = t("checkout.required");
    if (!form.last_name) e.last_name = t("checkout.required");
    if (!form.email) e.email = t("checkout.required");
    if (!form.address) e.address = t("checkout.required");
    if (!form.city) e.city = t("checkout.required");
    if (!form.zip) e.zip = t("checkout.required");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!user) { navigate("/auth"); return; }
    setLoading(true);
    try {
      const orderItems = items.map(i => ({ product_id: i.product.id, quantity: i.quantity }));
      const { data } = await axios.post(`${API}/orders`, {
        items: orderItems,
        shipping_address: { ...form },
        payment_method: "paypal",
        notes: form.notes
      }, { withCredentials: true });
      setOrderId(data.id);
      clearCart();
      setSuccess(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md px-6"
        >
          <div className="w-16 h-16 bg-foreground rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={24} strokeWidth={1.5} className="text-background" />
          </div>
          <h2 className="text-3xl font-heading font-bold tracking-tighter text-foreground mb-3">{t("checkout.success")}</h2>
          <p className="text-foreground/60 mb-3">{t("checkout.success.desc")}</p>
          {orderId && <p className="text-xs font-mono text-foreground/30 mb-8">ID: {orderId}</p>}
          <button
            data-testid="go-home-btn"
            onClick={() => navigate("/")}
            className="px-8 py-4 bg-foreground text-background text-sm font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all"
          >
            {lang === "de" ? "Zurück zum Showroom" : "Back to Showroom"}
          </button>
        </motion.div>
      </div>
    );
  }

  const handleFieldChange = (name, val) => {
    setForm(f => ({ ...f, [name]: val }));
    setErrors(er => ({ ...er, [name]: null }));
  };

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-heading font-bold tracking-tighter text-foreground mb-10"
        >
          {t("checkout.title")}
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-xs font-mono tracking-[0.2em] uppercase text-foreground/40 mb-6">{t("checkout.shipping")}</h2>
              <div className="grid grid-cols-2 gap-4">
                <CheckoutField name="first_name" label={t("checkout.first_name")} required value={form.first_name} error={errors.first_name} onChange={handleFieldChange} />
                <CheckoutField name="last_name" label={t("checkout.last_name")} required value={form.last_name} error={errors.last_name} onChange={handleFieldChange} />
                <CheckoutField name="email" label={t("checkout.email")} type="email" required value={form.email} error={errors.email} onChange={handleFieldChange} />
                <CheckoutField name="phone" label={t("checkout.phone")} value={form.phone} error={errors.phone} onChange={handleFieldChange} />
                <div className="col-span-2">
                  <CheckoutField name="address" label={t("checkout.address")} required value={form.address} error={errors.address} onChange={handleFieldChange} />
                </div>
                <CheckoutField name="zip" label={t("checkout.zip")} required value={form.zip} error={errors.zip} onChange={handleFieldChange} />
                <CheckoutField name="city" label={t("checkout.city")} required value={form.city} error={errors.city} onChange={handleFieldChange} />
                <div className="col-span-2">
                  <CheckoutField name="country" label={t("checkout.country")} value={form.country} error={errors.country} onChange={handleFieldChange} />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">{t("checkout.notes")}</label>
              <textarea
                data-testid="checkout-notes"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20 resize-none"
              />
            </div>

            <button
              data-testid="place-order-btn"
              type="submit"
              disabled={loading || items.length === 0}
              className="w-full py-4 bg-foreground text-background text-sm font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? (lang === "de" ? "Wird verarbeitet..." : "Processing...") : t("checkout.place_order")}
            </button>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="sticky top-28 bg-surface border border-border rounded-sm p-6"
            >
              <h3 className="text-xs font-mono tracking-widest uppercase text-foreground/40 mb-6">{t("checkout.summary")}</h3>
              <div className="space-y-3 mb-6">
                {items.map(item => {
                  const name = lang === "de" ? item.product.name_de : item.product.name_en;
                  return (
                    <div key={item.product.id} className="flex justify-between text-sm">
                      <span className="text-foreground/70 truncate flex-1">{name} ×{item.quantity}</span>
                      <span className="font-mono text-foreground ml-4">€{(item.product.price * item.quantity).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-border pt-4">
                <div className="flex justify-between">
                  <span className="font-medium text-foreground">{t("cart.total")}</span>
                  <span className="font-mono font-bold text-foreground text-lg">€{total.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        <div className="pb-24" />
      </div>
    </div>
  );
}
